<!--
 * =====================================================================
 * 작 업 명  : 회원가입
 * 파 일 명  : Join.vue
 * 변경이력
 * 2022-01-29 / 정태화 / 최초작성
 * 2022-02-15 / 경다찬 / 메일 발송, 수신 코드, ID 중복체크 작성
 * 2022-03-10 / 임태민 / 컴포넌트 분리, 모듈화
 * 2022-03-10 / 임태민 / 기능 및 유효성체크 추가, 수정
 * 2022-03-10 / 임태민 / 글자수 제한
 * 2022-10-17 / 정태화 / 사용자ID 소문자로 입력되게 처리
 * =====================================================================
 -->
<template>
  <div>
    <div class="text mt-3 mb-5">
      <p class="h4">
        <b>{{ $t("새 계정 생성") }}</b>
      </p>
    </div>
    <form ref="form" class="form-max-width" v-on:submit.prevent>
      <!-- name -->
      <div class="row">
        <div class="col-sm-6 mb-3">
          <div class="form-floating">
            <input
              type="text"
              class="form-control"
              id="first-name"
              v-model="this.userChild.FIRST_NAME"
              placeholder="First name"
              @input="onFirstNameInput"
              required
            />
            <label for="first-name"
              ><span class="required" aria-hidden="true">*</span>First
              name</label
            >
          </div>
          <div ref="FIRST_NAME" class="check" role="alert">&nbsp;</div>
        </div>
        <div class="col-sm-6 mb-3">
          <div class="form-floating">
            <input
              type="text"
              class="form-control"
              id="last-name"
              v-model="this.userChild.LAST_NAME"
              placeholder="Last name"
              @input="onLastNameInput"
              required
            />
            <label for="last-name"
              ><span class="required" aria-hidden="true">*</span>Last
              name</label
            >
          </div>
          <div ref="LAST_NAME" class="check" role="alert">&nbsp;</div>
        </div>
      </div>

      <!-- send code -->
      <div class="col mb-3">
        <div class="input-group">
          <div class="form-floating flex-grow-1">
            <input
              type="email"
              inputmode="email"
              class="form-control"
              id="email"
              v-model="this.userChild.EMAIL"
              placeholder="example@domain.com"
              required
              @input="onEmailInput"
            />
            <label for="email"
              ><span class="required" aria-hidden="true">*</span>Email
              address</label
            >
          </div>
        </div>
        <div ref="EMAIL" class="check" role="alert">&nbsp;</div>
      </div>

      <!-- user name(id) -->
      <div class="col mb-3">
        <div class="input-group">
          <div class="form-floating flex-grow-1">
            <input
              type="text"
              lang="en"
              class="form-control bg-white"
              id="user-id"
              v-model="this.userChild.USER_ID"
              placeholder="Username"
              required
              autocomplete="off"
              @input="onInput"
              @compositionstart="isComposing = true"
              @compositionend="onCompositionEnd"
            />
            <label for="user-id"
              ><span class="required" aria-hidden="true">*</span>Username
              (lowercase)</label
            >
          </div>
        </div>
        <div ref="USER_ID" class="check" role="alert">&nbsp;</div>
      </div>

      <!-- password -->
      <div class="row">
        <div class="col-sm-6 mb-3">
          <div class="form-floating input-group pw-group">
            <input
              :type="passwordFieldType"
              class="form-control"
              id="password"
              v-model="this.userChild.PASSWORD"
              placeholder="Password"
              required
              aria-describedby="password-requirements"
              @input="onPasswordInput"
            />
            <button
              type="button"
              class="btn border bg-white border border-radius-right rounded-end border-start-0"
              aria-label="Show/hide entered password"
              style="border: rgb(45, 38, 42)"
              @click="switchVisibility()"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="currentColor"
                class="bi bi-eye-fill"
                viewBox="0 0 16 16"
                v-show="iconShowPw"
              >
                <path d="M10.5 8a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0z" />
                <path
                  d="M0 8s3-5.5 8-5.5S16 8 16 8s-3 5.5-8 5.5S0 8 0 8zm8 3.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z"
                />
              </svg>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="currentColor"
                class="bi bi-eye-slash-fill"
                viewBox="0 0 16 16"
                v-show="iconHidePw"
              >
                <path
                  d="m10.79 12.912-1.614-1.615a3.5 3.5 0 0 1-4.474-4.474l-2.06-2.06C.938 6.278 0 8 0 8s3 5.5 8 5.5a7.029 7.029 0 0 0 2.79-.588zM5.21 3.088A7.028 7.028 0 0 1 8 2.5c5 0 8 5.5 8 5.5s-.939 1.721-2.641 3.238l-2.062-2.062a3.5 3.5 0 0 0-4.474-4.474L5.21 3.089z"
                />
                <path
                  d="M5.525 7.646a2.5 2.5 0 0 0 2.829 2.829l-2.83-2.829zm4.95.708-2.829-2.83a2.5 2.5 0 0 1 2.829 2.829zm3.171 6-12-12 .708-.708 12 12-.708.708z"
                />
              </svg>
            </button>
            <label for="password" style="z-index: 5"
              ><span class="required" aria-hidden="true">*</span>Password</label
            >
          </div>
          <div ref="PASSWORD" class="check" id="password-requirements"/>
        </div>
        <div class="col-sm-6 mb-3">
          <div class="form-floating">
            <input
              :type="passwordFieldType"
              class="form-control"
              id="password2"
              v-model="this.userChild.PASSWORD2"
              placeholder="Confirm password"
              required
              @input="onPassword2Input"
            />
            <label for="password2"
              ><span class="required" aria-hidden="true">*</span>Confirm
              password</label
            >
          </div>
          <div ref="PASSWORD2" class="check" role="alert">&nbsp;</div>
        </div>
      </div>

      <!-- buttons -->
      <div class="row">
        <div class="col mt-3 mb-3">
          <button
            type="button"
            class="btn btn-outline-dark btn-lg rounded-pill w-100"
            @click="
              cancelClick();
              closeWebView();
            "
          >
            Cancel
          </button>
        </div>
        <div class="col mt-3 mb-3"></div>
        <div class="col mt-3 mb-3">
          <button
            type="button"
            class="btn btn-dark btn-lg rounded-pill w-100"
            :disabled="!isFormValid"
            @click="next()"
          >
            Next
          </button>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import "@/assets/css/formLayout.css";
import { $axios, $error } from "@/dot-ui/index.js";
import { Join } from "@/js/user/Join.js";
import { router } from "@/router/index.js";

export default {
  props: ["user", "fromParams"],
  emits: ["prev", "changeData"],
  computed: {
    isFormValid() {
      const u = this.userChild;

      // 필수값 체크
      if (!u.FIRST_NAME || !u.LAST_NAME || !u.EMAIL ||
          !u.USER_ID || !u.PASSWORD || !u.PASSWORD2) {
        return false;
      }

      // 이메일 형식
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(u.EMAIL)) return false;

      // USER_ID 형식
      if (!/^[a-z0-9_-]{4,20}$/.test(u.USER_ID)) return false;

      // 비밀번호 규칙
      const pwResult = this.validatePassword(u.PASSWORD);
      if (!pwResult.valid) return false;

      // 비밀번호 일치
      if (u.PASSWORD !== u.PASSWORD2) return false;

      // 아이디 중복 체크
      if (!this.isUserIdAvailable) return false;

      return true;
    }
  },
  data() {
    return {
      userChild: {},
      passwordFieldType: "password",
      iconShowPw: false,
      iconHidePw: true,
      debounceTimer: null,
      isComposing: false,
      isUserIdAvailable: false,
    };
  },
  created() {
    this.userChild = this.user;
  },
  methods: {
    cancelClick() {
      router.go(-1);
    },

    async debounceCheckDuplicate() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.checkDuplicateId();
      }, 500);
    },

    onPasswordInput(event) {
      const pw = event.target.value;
      const result = this.validatePassword(pw);

      this.$refs.PASSWORD.innerHTML = result.message;

      if (result.valid) {
        Join.setColorClass(this.$refs.PASSWORD, "green");
      } else {
        Join.setColorClass(this.$refs.PASSWORD, "red");
      }
    },

    validatePassword(pw) {
      if (!pw) return { valid: false, message: "This field is required." };

      // 길이
      if (pw.length < 8 || pw.length > 16) {
        return {
          valid: false,
          message:
              "Use 8–16 characters with letters (case-sensitive), numbers, or special characters — combine at least two.",
        };
      }

      // 공백/탭/개행 금지 (스페셜 포함은 허용하되 공백류만 차단)
      if (/\s/.test(pw)) {
        return { valid: false, message: "Spaces are not allowed." };
      }

      // 종류 체크: 영문 / 숫자 / 특수
      const hasLetter = /[A-Za-z]/.test(pw);
      const hasDigit = /\d/.test(pw);
      const hasSpecial = /[^A-Za-z0-9]/.test(pw);

      const typeCount = Number(hasLetter) + Number(hasDigit) + Number(hasSpecial);

      if (typeCount < 2) {
        return {
          valid: false,
          message:
              "Use 8–16 characters with letters (case-sensitive), numbers, or special characters — combine at least two.",
        };
      }

      return { valid: true, message: "This password is available." };
    },

    onPassword2Input() {
      if (!this.userChild.PASSWORD2) {
        this.$refs.PASSWORD2.innerHTML = "";
        Join.setColorClass(this.$refs.PASSWORD2, "red");
        return;
      }

      if (this.userChild.PASSWORD !== this.userChild.PASSWORD2) {
        this.$refs.PASSWORD2.innerHTML = "Passwords do not match.";
        Join.setColorClass(this.$refs.PASSWORD2, "red");
      } else {
        this.$refs.PASSWORD2.innerHTML = "Passwords match.";
        Join.setColorClass(this.$refs.PASSWORD2, "green");
      }
    },

    // 비밀번호 show / hide
    switchVisibility() {
      this.passwordFieldType =
        this.passwordFieldType === "password" ? "text" : "password";
      this.iconShowPw = this.iconShowPw !== true;
      this.iconHidePw = this.iconHidePw === false;
    },
    changeData() {
      this.$emit("changeData", this.userChild, "showJoin2");
    },
    trimStringValues: function () {
      Object.entries(this.userChild).forEach(([key, value]) => {
        if (typeof value === "string") {
          this.userChild[key] = value.trim();
        }
      });
    },
    onFirstNameInput() {
      const el = this.$refs.FIRST_NAME;
      if (el) {
        el.innerHTML = "";          // 서버 에러 문구 제거
        Join.setColorClass(el, ""); // red/green/blue 제거 → 테두리 기본으로
      }
    },
    onLastNameInput() {
      const el = this.$refs.LAST_NAME;
      if (el) {
        el.innerHTML = "";
        Join.setColorClass(el, "");
      }
    },
    onEmailInput() {
      // v-model이 이미 값은 잡고 있으니 여기선 메시지/색만 초기화
      const el = this.$refs.EMAIL;
      if (el) {
        el.innerHTML = "";             // 밑 텍스트 제거
        Join.setColorClass(el, "");    // 색상 클래스 제거 → 테두리도 기본으로 복귀
      }
    },
    // next 클릭 시 유효성 검사 후 다음 페이지로 넘어가기
    async next() {
      let fromSiteNo = this.fromParams.s;
      let fromCompNo = this.fromParams.c;

      this.userChild.SITE_NO = fromSiteNo;
      this.userChild.COMP_NO = fromCompNo;

      $error.clearForm("check");
      this.trimStringValues();

      let hasError = false;

      // 1) FIRST_NAME required
      if (!this.userChild.FIRST_NAME) {
        this.$refs.FIRST_NAME.innerHTML = "First name is required.";
        Join.setColorClass(this.$refs.FIRST_NAME, "red");
        hasError = true;
      }

      // 2) LAST_NAME required
      if (!this.userChild.LAST_NAME) {
        this.$refs.LAST_NAME.innerHTML = "Last name is required.";
        Join.setColorClass(this.$refs.LAST_NAME, "red");
        hasError = true;
      }

      // 3) EMAIL required + 간단 형식 체크 (옵션)
      if (!this.userChild.EMAIL) {
        this.$refs.EMAIL.innerHTML = "Email address is required.";
        Join.setColorClass(this.$refs.EMAIL, "red");
        hasError = true;
      } else {
        // 간단 이메일 형식 체크를 하고 싶다면 (옵션)
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailPattern.test(this.userChild.EMAIL)) {
          this.$refs.EMAIL.innerHTML = "Enter a valid email address.";
          Join.setColorClass(this.$refs.EMAIL, "red");
          hasError = true;
        }
      }

      // 4) USER_ID required (형식 체크는 이미 onInput에서 하고 있음)
      if (!this.userChild.USER_ID) {
        this.$refs.USER_ID.innerHTML = "Username is required.";
        Join.setColorClass(this.$refs.USER_ID, "red");
        hasError = true;
      }

      // 5) PASSWORD 프론트 검증 재사용
      const pwResult = this.validatePassword(this.userChild.PASSWORD);
      if (!pwResult.valid) {
        this.$refs.PASSWORD.innerHTML = pwResult.message;
        Join.setColorClass(this.$refs.PASSWORD, "red");
        hasError = true;
      }

      // 6) PASSWORD2 체크
      if (!this.userChild.PASSWORD2) {
        this.$refs.PASSWORD2.innerHTML = "This field is required.";
        Join.setColorClass(this.$refs.PASSWORD2, "red");
        hasError = true;
      } else if (this.userChild.PASSWORD !== this.userChild.PASSWORD2) {
        this.$refs.PASSWORD2.innerHTML = "Passwords do not match.";
        Join.setColorClass(this.$refs.PASSWORD2, "red");
        hasError = true;
      }

      if (hasError) {
        if (this.$refs.form) {
          this.$refs.form.classList.add("was-validated");
        }
        return;
      }

      if (this.$refs.form) {
        this.$refs.form.classList.add("was-validated");
      }

      let url = "/user-app/v2/users/pre-check";
      let param = this.userChild;
      let response = await $axios.post(url, param);

      if (response.status == 200) {
        this.changeData();
      } else {
        $error.showMessage(response, this);
        $error.focus(response);
        response.data?.errors?.forEach((e) => {
          const el = this.$refs[e.code] ?? this.$refs["ERRORS"];
          if (el) Join.setColorClass(el, "red");
        });
      }
    },

    onInput(event) {
      if (this.isComposing) return;
      this.filterAndValidateUserId(event);
    },

    // 한글 입력이 끝난 순간 처리
    onCompositionEnd(event) {
      this.isComposing = false;
      this.filterAndValidateUserId(event);
    },

    // 필터링 + 유효성검사 + 중복체크까지 포함
    filterAndValidateUserId(event) {
      let value = event.target.value.toLowerCase();
      value = value.replace(/[^a-z0-9_-]/g, ""); // 영문, 숫자, -, _ 만 허용
      event.target.value = value;
      this.userChild.USER_ID = value;

      // 유효성 표시
      if (!/^[a-z0-9_-]{4,20}$/.test(value)) {
        this.$refs.USER_ID.innerHTML =
            "Use 4–20 lowercase letters, numbers, '-' or '_'.";
        Join.setColorClass(this.$refs.USER_ID, "red");
        return; // 형식이 안 맞으면 중복체크 중단
      } else {
        this.$refs.USER_ID.innerHTML = "Valid format.";
        Join.setColorClass(this.$refs.USER_ID, "blue");
      }

      // ✅ 형식이 유효하면 중복체크 실행 (디바운스)
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.checkDuplicateId();
      }, 500);
    },

    // 아이디 중복 확인
    async checkDuplicateId() {
      try {
        let fromSiteNo = this.fromParams.s;
        let fromCompNo = this.fromParams.c;
        const userId = this.userChild.USER_ID;
        if (!/^[a-z0-9_-]{4,20}$/.test(userId)) return;

        let url = `/user-app/v2/users/id-exists?SITE_NO=${fromSiteNo}&COMP_NO=${fromCompNo}&USER_ID=${userId}`;
        let response = await $axios.get(url, this.userChild);
        let json = response.data;

        if (json.RESULT_CODE == 2) {
          this.$refs.USER_ID.innerHTML = `That username isn’t available. Please choose another one.`;
          Join.setColorClass(this.$refs.USER_ID, "red");
          this.isUserIdAvailable = false;
        } else if (json.RESULT_CODE == 1) {
          this.$refs.USER_ID.innerHTML = `${userId} is available.`;
          Join.setColorClass(this.$refs.USER_ID, "green");
          this.isUserIdAvailable = true;
        }
      } catch (e) {
        console.error("Duplicate check failed:", e);
        if (this.$refs.USER_ID)
          this.$refs.USER_ID.innerHTML = "Network error. Try again later.";
      }
    },

    closeWebView() {
      if (window.webkit) {
        window.webkit.messageHandlers.join.postMessage("close");
      }
    },
  },
};
</script>

<style scoped>
.form-max-width {
  max-width: 500px;
  width: 500px;
  height: 480px;
  margin: 0 auto;
}

/* 부트스트랩/HTML5 기본 valid/invalid 테두리 스타일 죽이기 */
.form-max-width.was-validated .form-floating .form-control:valid,
.form-max-width.was-validated .form-floating .form-control:invalid,
.form-max-width.was-validated .input-group .form-control:valid,
.form-max-width.was-validated .input-group .form-control:invalid {
  border-color: #ced4da;
  box-shadow: none;
}

/* 에러(red)일 때 */
.form-floating:has(+ .check.red) .form-control,
.input-group:has(+ .check.red) .form-control {
  border-color: #dc3545 !important;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
}

/* 성공(green)일 때 */
.form-floating:has(+ .check.green) .form-control,
.input-group:has(+ .check.green) .form-control {
  border-color: #198754 !important;
  box-shadow: 0 0 0 0.2rem rgba(25, 135, 84, 0.25);
}

/* 정보(blue)일 때 */
.form-floating:has(+ .check.blue) .form-control,
.input-group:has(+ .check.blue) .form-control {
  border-color: #0d6efd !important;
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
}

/* 외곽 박스는 pw-group 하나만 사용 */
.pw-group {
  border: 1px solid #ced4da;
  border-radius: 0.375rem; /* bootstrap 기본 form-control radius */
}

/* 안쪽 input / button 은 border/그림자 제거 */
.pw-group .form-control,
.pw-group .btn {
  border: 0 !important;
}

/* 에러(red)일 때 – 그룹 전체에만 적용 */
.pw-group:has(+ #password-requirements.red) {
  border-color: #dc3545 !important;
}

/* 성공(green)일 때 – 그룹 전체에만 적용 */
.pw-group:has(+ #password-requirements.green) {
  border-color: #198754 !important;
}
</style>
